import React, { useEffect, useState } from "react";
import apiClient from "../../lib/api";
import { useI18n } from "../../i18n";
import { toast } from "sonner";
import { Send, Mail, AlertCircle, CheckCircle2 } from "lucide-react";

const STATUS = {
  sent: { color: "bg-olive/20 text-olive", icon: CheckCircle2 },
  queued: { color: "bg-secondary-warm text-ink", icon: Mail },
  failed: { color: "bg-terracotta/15 text-terracotta", icon: AlertCircle },
};

const KIND_LABELS = {
  order_confirmation: "Order confirmation",
  admin_notification: "Admin notification",
  shipped: "Shipped",
  delivered: "Delivered",
};

const AdminEmails = () => {
  const { lang } = useI18n();
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [testEmail, setTestEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [emailCfg, setEmailCfg] = useState({ resend_api_key: "", from_email: "", admin_notify_email: "" });

  const load = () => apiClient.get("/admin/emails").then((r) => setItems(r.data));
  useEffect(() => {
    load();
    apiClient.get("/admin/settings").then((r) => setEmailCfg(r.data.email || {}));
  }, []);

  const openDetail = async (id) => {
    const { data } = await apiClient.get(`/admin/emails/${id}`);
    setSelected(data);
  };

  const sendTest = async (e) => {
    e.preventDefault();
    if (!testEmail) return;
    setSending(true);
    try {
      const { data } = await apiClient.post("/admin/emails/test", { to: testEmail });
      if (data.configured) toast.success(lang === "nl" ? `Verzonden naar ${testEmail}` : `Sent to ${testEmail}`);
      else toast.message(lang === "nl" ? "Email staat in wachtrij — voeg een Resend API key toe in Instellingen om echt te versturen." : "Email queued — add a Resend API key in Settings to actually send.", { duration: 8000 });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setSending(false);
    }
  };

  const configured = !!emailCfg.resend_api_key;

  return (
    <div data-testid="admin-emails-page">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
        <h1 className="font-display text-4xl md:text-5xl tracking-tighter font-light">{lang === "nl" ? "Emails" : "Emails"}</h1>
        <div className={`text-xs font-label px-3 py-1.5 ${configured ? "bg-olive/15 text-olive" : "bg-terracotta/10 text-terracotta"}`}>
          {configured ? (lang === "nl" ? "RESEND ACTIEF" : "RESEND ACTIVE") : (lang === "nl" ? "GEEN API KEY — EMAILS IN WACHTRIJ" : "NO API KEY — EMAILS QUEUED")}
        </div>
      </div>

      <div className="border border-black/10 bg-white p-6 mb-6">
        <div className="font-label text-ink-muted mb-3">{lang === "nl" ? "Test email" : "Send test email"}</div>
        <form onSubmit={sendTest} className="flex gap-2 items-center flex-wrap">
          <input type="email" required value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="you@example.com" className="border border-black/15 px-3 py-2 text-sm flex-1 min-w-[200px]" data-testid="emails-test-input" />
          <button disabled={sending} className="btn-primary text-sm" data-testid="emails-test-send"><Send className="w-4 h-4" /> {sending ? "…" : (lang === "nl" ? "Verstuur test" : "Send test")}</button>
        </form>
        {!configured && <p className="text-xs text-ink-muted mt-2">{lang === "nl" ? "Tip: voeg je Resend API key toe in" : "Tip: add your Resend API key in"} <a href="/admin/settings" className="underline">{lang === "nl" ? "Instellingen → Email" : "Settings → Email"}</a>.</p>}
      </div>

      <div className="grid md:grid-cols-12 gap-6">
        <div className="md:col-span-7 border border-black/10 bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary-warm text-left">
              <tr><th className="px-4 py-3 font-label">When</th><th className="px-4 py-3 font-label">Kind</th><th className="px-4 py-3 font-label">To</th><th className="px-4 py-3 font-label">Status</th></tr>
            </thead>
            <tbody className="divide-y divide-black/5">
              {items.map((e) => {
                const S = STATUS[e.status] || STATUS.queued;
                return (
                  <tr key={e.email_id} onClick={() => openDetail(e.email_id)} className="hover:bg-secondary-warm/40 cursor-pointer" data-testid={`emails-row-${e.email_id}`}>
                    <td className="px-4 py-3 text-xs text-ink-muted">{e.created_at?.slice(0, 19).replace("T", " ")}</td>
                    <td className="px-4 py-3"><span className="font-label text-xs">{KIND_LABELS[e.kind] || e.kind}</span></td>
                    <td className="px-4 py-3 text-xs">{e.to}</td>
                    <td className="px-4 py-3"><span className={`font-label px-2 py-1 text-[10px] inline-flex items-center gap-1 ${S.color}`}><S.icon className="w-3 h-3" /> {e.status}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {items.length === 0 && <div className="p-10 text-center text-ink-muted">{lang === "nl" ? "Nog geen emails verstuurd." : "No emails yet."}</div>}
        </div>
        <div className="md:col-span-5">
          {!selected && <div className="border border-black/10 bg-white p-6 text-ink-muted text-sm">{lang === "nl" ? "Selecteer een email om te bekijken" : "Select an email to preview"}</div>}
          {selected && (
            <div className="border border-black/10 bg-white" data-testid="email-preview">
              <div className="p-4 border-b border-black/5 flex justify-between gap-2 flex-wrap">
                <div>
                  <div className="font-display text-lg leading-tight">{selected.subject}</div>
                  <div className="text-xs text-ink-muted mt-1">to {selected.to} · {selected.kind} · {selected.status}</div>
                  {selected.error && <div className="text-xs text-terracotta mt-1">{selected.error}</div>}
                </div>
                <button onClick={() => setSelected(null)} className="text-ink-muted text-sm">close</button>
              </div>
              <iframe title="preview" srcDoc={selected.html} className="w-full h-[60vh] bg-bone" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminEmails;
